import { useEffect } from 'react';
import { Drawer } from '@/components/common/Drawer';
import { useStation } from '@/context/StationContext';
import { useFilters } from '@/context/FiltersContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { StationHeader } from './StationHeader';
import { PriceCard } from './PriceCard';
import { PriceHistory } from './PriceHistory';
import { StationInfo } from './StationInfo';
import { StationActions } from './StationActions';

interface StationDrawerProps {
  className?: string;
  allStations?: Array<{ id: string; prices: Record<string, { price: number } | null> }>;
}

export function StationDrawer({ className, allStations = [] }: StationDrawerProps) {
  const { selectedStation, isDrawerOpen, closeDrawer } = useStation();
  const { selectedFuelTypes, primaryFuelType } = useFilters();
  const isMobile = useIsMobile();

  // Announce to screen readers when drawer opens
  useEffect(() => {
    if (selectedStation && isDrawerOpen) {
      const announcement = `Station details for ${selectedStation.name}`;
      // Create and dispatch announcement
      const liveRegion = document.getElementById('live-region');
      if (liveRegion) {
        liveRegion.textContent = announcement;
      }
    }
  }, [selectedStation, isDrawerOpen]);

  if (!selectedStation) {
    return null;
  }

  // Calculate price ranks for each fuel type
  const priceRanks = calculatePriceRanks(selectedStation.id, primaryFuelType, allStations);

  // Sort fuel types to show primary first, then selected ones
  const sortedFuelTypes = [...selectedFuelTypes].sort((a, b) => {
    if (a === primaryFuelType) return -1;
    if (b === primaryFuelType) return 1;
    return 0;
  });

  return (
    <>
      {/* Screen reader announcement region */}
      <div id="live-region" className="sr-only" aria-live="polite" aria-atomic="true" />

      <Drawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        position={isMobile ? 'bottom' : 'right'}
        title={selectedStation.name}
        className={className}
      >
        <div className="p-4 space-y-6">
          {/* Header with station name, brand, status */}
          <StationHeader station={selectedStation} />

          {/* Price cards for selected fuel types */}
          <section aria-label="Current prices">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Current Prices</h3>
            <div className="grid grid-cols-2 gap-3">
              {sortedFuelTypes.map((fuelType) => (
                <PriceCard
                  key={fuelType}
                  fuelType={fuelType}
                  priceInfo={selectedStation.prices[fuelType]}
                  isPrimary={fuelType === primaryFuelType}
                  rank={priceRanks.get(fuelType)}
                  averagePrice={calculateAveragePrice(fuelType, allStations)}
                />
              ))}
            </div>
          </section>

          {/* Price history chart */}
          <PriceHistory stationId={selectedStation.id} />

          {/* Station info - address, hours, amenities */}
          <StationInfo station={selectedStation} />

          {/* Actions - share, report */}
          <StationActions station={selectedStation} />
        </div>
      </Drawer>
    </>
  );
}

// Helper to calculate price rank for a station
function calculatePriceRanks(
  stationId: string,
  primaryFuelType: string,
  allStations: Array<{ id: string; prices: Record<string, { price: number } | null> }>
): Map<string, number> {
  const ranks = new Map<string, number>();

  // Get all prices for the primary fuel type and sort
  const stationsWithPrice = allStations
    .filter((s) => s.prices[primaryFuelType]?.price != null)
    .sort((a, b) => {
      const priceA = a.prices[primaryFuelType]?.price ?? Infinity;
      const priceB = b.prices[primaryFuelType]?.price ?? Infinity;
      return priceA - priceB;
    });

  const index = stationsWithPrice.findIndex((s) => s.id === stationId);
  if (index >= 0) {
    ranks.set(primaryFuelType, index + 1);
  }

  return ranks;
}

// Helper to calculate average price for a fuel type
function calculateAveragePrice(
  fuelType: string,
  allStations: Array<{ prices: Record<string, { price: number } | null> }>
): number | null {
  const prices = allStations
    .map((s) => s.prices[fuelType]?.price)
    .filter((p): p is number => p != null);

  if (prices.length === 0) return null;
  return prices.reduce((sum, p) => sum + p, 0) / prices.length;
}
